import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ITaskOptions} from "../../../../models/task-options.model";
import {MatAccordion} from "@angular/material/expansion";
import {MaterialModule} from "../../../../material.module";
import {DatePipe, DecimalPipe, TitleCasePipe} from "@angular/common";
import {ListingsComponent} from "../../../objects/listings/listings.component";
import {ClientService} from "../../../../services/auth/client.service";
import {MatDialog} from "@angular/material/dialog";
import {TasksService} from "../../../../services/objects/tasks.service";
import {ConfirmDialogComponent} from "../../../confirm-dialog/confirm-dialog.component";
import {ToastService} from "../../../../services/layout/toast.service";
import {TasksGeneralSettingsComponent} from "../task-general-settings/task-general-settings.component";
import {NotificationSettingsComponent} from "../../../objects/notifications/notification-settings.component";
import {ScheduleSettingsComponent} from "../../../objects/scheduling/schedule-settings.component";
import {Marked} from "marked";
import {TaskMetric} from "../../reports/task-metrics.model";
import {TaskReportComponent} from "../../reports/task-report.component";
import {TabbedPageComponent} from "../../../objects/tabbed-page/tabbed-page.component";
import {environment} from "../../../../../environments/environment";
import {ClientObjectSettings} from "../../../objects/client-object-settings/client-object-settings.component";
import {TaskRunnerComponent} from "../task-runner/task-runner.component";
import {TaskPreviousRunsComponent} from "../tasks-previous-runs/task-previous-runs.component";

@Component({
  selector: 'app-task-settings',
  templateUrl: './task-settings.component.html',
  styleUrls: ['./task-settings.component.scss'],
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    ListingsComponent,
    TasksGeneralSettingsComponent,
    NotificationSettingsComponent,
    ScheduleSettingsComponent,
    FormsModule,
    DecimalPipe,
    TitleCasePipe,
    DatePipe,
    TaskReportComponent,
    ClientObjectSettings,
    TaskRunnerComponent,
    TaskPreviousRunsComponent
  ],
  providers: [Marked]
})
export class TaskSettingsComponent extends TabbedPageComponent implements OnInit {
    clientService = inject(ClientService);
    private fb = inject(FormBuilder);
    private tasksService = inject(TasksService);
    private dialog = inject(MatDialog);
    private snackBar = inject(ToastService);

  isSaving: boolean = false;
  isDeleting: boolean = false;

  task: ITaskOptions | null = null;
  reportData: TaskMetric[] | null = null;
  id: string = '';

  files: string[] = [];
  functions: string[] = [];
  tasks: string[] = [];

  allTasks: any[];

  public displayedColumns: string[] = [
    'reportDate',
    'taskCallType',
    'model',
    'responseType',
    'totalTaskCalled',
    'totalTaskCompleted',
    'totalTaskFailed',
    'avgTaskTimeTaken'
  ];

  // Reactive Forms
  settingsForm: FormGroup;
  notificationsForm: FormGroup;
  scheduleSettingsForm: FormGroup;
  changeToRefs: boolean = false;

  @ViewChild(MatAccordion) accordion: MatAccordion;

  ngOnInit(): void {
    super.OnInit()
    this.settingsForm = this.fb.group({
      instructions: ['', Validators.required],
      model: ['', Validators.required],
      responseType: ['', Validators.required],
      temperature: [0.3, Validators.required],
      reasoningEffortLevel: ["Low", Validators.required]
    });
    this.notificationsForm = this.fb.group({});
    this.scheduleSettingsForm = this.fb.group({});

    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.loadData();
    });
  }

  isFormTouched(): boolean {
    if (this.scheduleSettingsForm.touched) return true;
    if (this.notificationsForm.touched) return true;
    if (this.settingsForm.touched) return true;
    return false;
  }

  onFileRefsChange(updatedRefs: any[]): void {
    this.task!.fileRefs = updatedRefs;
    this.changeToRefs = true;
  }

  onFunctionRefsChange(updatedRefs: any[]): void {
    this.task!.functionRefs = updatedRefs;
    this.changeToRefs = true;
  }

  onTaskChange(updatedRefs: any[]): void {
    this.task!.completionRefs = updatedRefs;
    this.changeToRefs = true;
  }

  async loadData() {
    try {
      // Fetch Task Data
      const taskResponse = await this.tasksService.getByIdAsync(this.id).toPromise();
      const allTasks = await this.tasksService.getAllTasksAsync(this.id).toPromise();
      this.allTasks = allTasks as any[]

      this.task = taskResponse as ITaskOptions;
      // Populate Settings Form
      this.settingsForm.patchValue({
        instructions: this.task?.instructions,
        model: this.task?.model,
        responseType: this.task?.responseType,
        temperature: this.task?.temperature,
        reasoningEffortLevel: this.task?.reasoningEffortLevel
      });

      // Fetch Tasks and Master Threads
      this.AddFiles();
      this.AddFunctions();
      this.AddCompletions();

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      const endDate = new Date();

      this.tasksService.reportAsync(this.task?.id ?? "", startDate.toISOString(), endDate.toISOString()).subscribe((data: TaskMetric[]) => {
        this.reportData = data;
      });

    } catch (error) {
      console.error('Error loading data', error);
      this.snackBar.showError('Error loading data', 3000);
    }
  }

  AddFiles() {
    this.files = this.task?.fileRefs ?? [];
  }

  AddFunctions() {
    this.functions = this.task?.functionRefs ?? [];
  }

  AddCompletions() {
    this.tasks = this.task?.completionRefs ?? [];
  }

  save() {
    if (!this.task) return;

    if (this.settingsForm.invalid) {
      this.snackBar.showError('Please fill in all required fields', 3000);
      return;
    }
    if (this.notificationsForm.invalid) {
      this.snackBar.showError('Please fill in all required fields', 3000);
      return;
    }

    this.isSaving = true;

    Object.assign(this.task!, this.settingsForm.value);
    this.task.scheduleSettings = this.scheduleSettingsForm.value;
    this.task.notificationSettings = this.notificationsForm.value;

    if (this.task.responseType === 'json' && this.task.instructions.indexOf('JSON') === -1) {
      this.snackBar.showError('Please include JSON in the instructions for JSON response type', 3000);
      this.isSaving = false;
      return;
    }

    // Send the save request
    this.tasksService.saveAsync(this.task).subscribe(() => {
      this.snackBar.showSuccess('Settings saved successfully', 3000);
      this.isSaving = false;
    }, error => {
      console.error('Error saving settings', error);
      this.snackBar.showError('Error saving settings', 3000);
      this.isSaving = false;
    });
  }

  delete() {
    if (!this.task) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Are you sure you want to delete this?',
        message: 'This will delete the record. Once deleted, it cannot be rolled back.',
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isDeleting = true;
        this.snackBar.showInfo('Deleting task...', 3000);
        this.tasksService.deleteAsync(this.task?.id ?? "").subscribe(() => {
          this.snackBar.showSuccess('Task deleted successfully.', 3000);
          this.router.navigate(['/ai/tasks']);
        }, error => {
          console.error('Error deleting task', error);
          this.snackBar.showError('Error deleting task.', 3000);
          this.isDeleting = false;
        });
      } else {
        this.snackBar.showInfo('Delete action canceled.', 3000);
      }
    });
  }

  protected readonly environment = environment;
}
